import { CommonModule } from '@angular/common';
import { ColorPickerModule } from 'ngx-color-picker';
import {
  Component,
  computed,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AbbSimpleCardComponent } from '@components/abb-card/abb-simple-card.component';
import {
  languageListQuery,
  languageSettingsMutation,
  languageSettingsQuery,
  regionSettingsQuery,
  systemSettingsQuery,
  uiSettingsMutation,
  uiSettingsQuery,
} from '@graphql/general';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { Apollo } from 'apollo-angular';
import {
  LanguageListQuery,
  LanguageSettingsQuery,
  RegionSettings,
  RegionSettingsQuery,
  SystemSettingsQuery,
  SystemSettingsQueryVariables,
  UiSettings,
  UiSettingsQuery,
  UpdateLanguageSettingsMutation,
  UpdateLanguageSettingsMutationVariables,
  UpdateUiSettingsMutation,
  UpdateUiSettingsMutationVariables,
} from 'gql/graphql';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { CognitoService, UserWithID } from '@services/cognito/cognito.service';
import { TranslateService } from '@ngx-translate/core';
import { mapIdToFileName } from '@constants/settings';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { ABBLogoComponent } from '@components/abb-logo/abb-logo';
import { ThemeService } from '@services/ui/theme.service';
import { FilterOption } from '@components/abb-table/abb-table.component';
import { UserSettingsService } from '@services/user/user-settings.service';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import {
  SystemSettings,
  SystemSettingsModalComponent,
} from './general-modals/system-modal.component';
import { OverlayService } from '@services/ui/overlay.service';
import { AbbDateComponent } from '../../../../components/abb-date/abb-date.component';
import { RegionSettingsModalComponent } from './general-modals/region-settings-modal.component';
import { t } from '@utils/strings';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'abb-general',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    AbbSimpleCardComponent,
    TPipe,
    MatCheckboxModule,
    AbbIconComponent,
    ABBLogoComponent,
    ColorPickerModule,
    AbbDateComponent,
    MatProgressSpinnerModule,
  ],
  template: `
    <div
      class="w-full h-auto grid grid-cols-1 lg:grid-cols-2 grid-flow-row-dense gap-2 pr-4"
    >
      <div class="col-span-1 p-4 flex flex-col gap-8">
        <abb-simple-card
          [title]="'Region' | t"
          [editable]="true"
          [editWithModal]="true"
          (edit)="openEditRegionModal()"
          class="flex-1"
        >
          <div
            content
            class="flex-1 w-full flex flex-row gap-3 px-3 font-medium text-xs text-abb-font/80"
          >
            <div class="w-full flex flex-col flex-1 gap-1 justify-evenly">
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Web browser time' | t }}</span>
                <abb-date
                  [date]="webBrowserTime()"
                  [showTime]="true"
                  [showSeconds]="true"
                  [timePosition]="'right'"
                  [timezone]="currentBrowserTimezone()"
                />
              </div>
                 <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Current browser timezone' | t }}</span>
                <span class="text-right" [style.word-break]="'break-word'">{{
                  currentBrowserTimezone()
                }}</span>
              </div>
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Currency' | t }}</span>
                <span class="text-right" [style.word-break]="'break-word'">{{
                  regionSettings$.value?.currency?.value
                }}</span>
              </div>
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Timezone' | t }}</span>
                <span class="text-right" [style.word-break]="'break-word'">{{
                  regionSettings$.value?.timezone?.zone
                }}</span>
              </div>
            </div>
          </div>
        </abb-simple-card>
      </div>
      <div class="col-span-1 p-4 flex flex-col gap-8">
        <abb-simple-card
          #uiAspectCard
          [title]="'UI Aspect' | t"
          [editable]="true"
          (edit)="editUIAspect($event)"
          class="flex-1"
        >
          <div
            content
            [formGroup]="uiAspectForm"
            class="w-full flex flex-row gap-3 px-3 font-medium text-xs text-abb-font/80"
          >
            <table class="min-w-full overflow-auto">
              <thead>
                <tr class="uppercase">
                  <th class="py-2 text-left"></th>
                  <th class="py-2 text-center">{{ 'ABB' | t }}</th>
                  <th class="py-2 text-center">{{ 'Custom' | t }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-left">
                    {{ 'Background color' | t }}
                  </td>
                  <td class="text-center">
                    <mat-checkbox
                      formControlName="abbBackgroundColor"
                      (change)="
                        backgroundColorChange($event, 'abbBackgroundColor')
                      "
                      name="abb-background-color"
                      color="primary"
                      class="border-font-checkbox no-hover-checkbox"
                    />
                  </td>
                  <td class="text-center">
                    <div class="flex flex-row justify-center items-center">
                      <mat-checkbox
                        [checked]="
                          !uiAspectForm.controls.abbBackgroundColor.value
                        "
                        (change)="
                          backgroundColorChange($event, 'customBackgroundColor')
                        "
                        [disabled]="
                          uiAspectForm.controls.customBackgroundColor.disabled
                        "
                        name="custom-background-color"
                        color="primary"
                        class="border-font-checkbox no-hover-checkbox"
                      />
                      <div
                        class="icon-container"
                        [ngClass]="
                          uiAspectForm.controls.abbBackgroundColor.value
                            ? '!cursor-default !pointer-events-none !text-abb-font/40'
                            : 'text-abb-font/85'
                        "
                      >
                        <abb-icon
                          [name]="'mat/colorize'"
                          (colorPickerSelect)="
                            colorSelect($event, 'background')
                          "
                          [cpAlphaChannel]="'disabled'"
                          [cpOutputFormat]="'rgba'"
                          [cpCancelButton]="true"
                          [cpOKButton]="true"
                          [cpSaveClickOutside]="false"
                          [(colorPicker)]="
                            uiAspectForm.value.customBackgroundColor!
                          "
                        />
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="text-left">
                    {{ 'Theme color' | t }}
                  </td>
                  <td class="text-center">
                    <mat-checkbox
                      formControlName="abbThemeColor"
                      (change)="themeColorChange($event, 'abbThemeColor')"
                      name="abb-theme-color"
                      color="primary"
                      class="border-font-checkbox no-hover-checkbox"
                    />
                  </td>
                  <td class="text-center">
                    <div class="flex flex-row justify-center items-center">
                      <mat-checkbox
                        [checked]="!uiAspectForm.controls.abbThemeColor.value"
                        (change)="themeColorChange($event, 'customThemeColor')"
                        [disabled]="
                          uiAspectForm.controls.customThemeColor.disabled
                        "
                        name="custom-theme-color"
                        color="primary"
                        class="border-font-checkbox no-hover-checkbox"
                      />
                      <div
                        class="icon-container"
                        [ngClass]="
                          uiAspectForm.controls.abbThemeColor.value
                            ? '!cursor-default !pointer-events-none !text-abb-font/40'
                            : 'text-abb-font/85'
                        "
                      >
                        <abb-icon
                          [name]="'mat/colorize'"
                          (colorPickerSelect)="colorSelect($event, 'theme')"
                          [cpAlphaChannel]="'disabled'"
                          [cpOutputFormat]="'rgba'"
                          [cpCancelButton]="true"
                          [cpOKButton]="true"
                          [cpSaveClickOutside]="false"
                          [(colorPicker)]="uiAspectForm.value.customThemeColor!"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="text-left">
                    {{ 'Text color' | t }}
                  </td>
                  <td class="text-center">
                    <mat-checkbox
                      formControlName="abbTextColor"
                      (change)="textColorChange($event, 'abbTextColor')"
                      name="abb-text-color"
                      color="primary"
                      class="border-font-checkbox no-hover-checkbox"
                    />
                  </td>
                  <td class="text-center">
                    <mat-checkbox
                      [checked]="!uiAspectForm.controls.abbTextColor.value"
                      (change)="textColorChange($event, 'customTextColor')"
                      [disabled]="
                        uiAspectForm.controls.customTextColor.disabled
                      "
                      name="custom-text-color"
                      color="primary"
                      class="border-font-checkbox no-hover-checkbox"
                    />
                    <div
                      class="icon-container"
                      [ngClass]="
                        uiAspectForm.controls.abbTextColor.value
                          ? '!cursor-default !pointer-events-none !text-abb-font/40'
                          : 'text-abb-font/85'
                      "
                    >
                      <abb-icon
                        #textColorPicker
                        [name]="'mat/colorize'"
                        (colorPickerSelect)="colorSelect($event, 'text')"
                        [cpAlphaChannel]="'disabled'"
                        [cpOutputFormat]="'rgba'"
                        [cpCancelButton]="true"
                        [cpOKButton]="true"
                        [cpSaveClickOutside]="false"
                        [(colorPicker)]="uiAspectForm.value.customTextColor!"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="text-left">
                    {{ 'Logo' | t }}
                  </td>
                  <td class="justify-items-center">
                    <div
                      [ngClass]="{
                        '!border-abb-primary !border-solid':
                          !abbLogoSelected() &&
                          !uiAspectForm.controls.customLogo.disabled,
                        'image-upload-container':
                          !uiAspectForm.controls.customLogo.disabled,
                        'opacity-30':
                          abbLogoSelected() &&
                          uiAspectForm.controls.customLogo.disabled
                      }"
                      class="rounded w-12 h-12 p-1"
                      (click)="selectABBLogo()"
                      [ngClass]="{
                        '!border-abb-primary !border-solid':
                          abbLogoSelected() &&
                          !uiAspectForm.controls.customLogo.disabled,
                        'image-upload-container cursor-pointer':
                          !uiAspectForm.controls.customLogo.disabled,
                        'opacity-30':
                          !abbLogoSelected() &&
                          uiAspectForm.controls.customLogo.disabled
                      }"
                    >
                      <abb-logo
                        class="block w-full h-full content-center"
                        svgClassName="w-full h-2/4"
                      />
                    </div>
                  </td>
                  <td class="justify-items-center">
                    <div
                      class="rounded w-12 h-12 bg-abb-black/[0.059]"
                      [ngClass]="{
                        '!border-abb-primary !border-solid':
                          !abbLogoSelected() &&
                          !uiAspectForm.controls.customLogo.disabled,
                        'image-upload-container':
                          !uiAspectForm.controls.customLogo.disabled,
                        'opacity-30':
                          abbLogoSelected() &&
                          uiAspectForm.controls.customLogo.disabled
                      }"
                    >
                      <label
                        for="imageUpload"
                        class="w-full h-full flex items-center justify-center"
                        [ngClass]="{
                          'cursor-pointer':
                            !uiAspectForm.controls.customLogo.disabled
                        }"
                      >
                        @if(imagePreview){
                        <img
                          [src]="imagePreview"
                          alt="Image Preview"
                          class="preview-image"
                        />
                        } @else {
                        <abb-icon
                          [name]="'mat-outlined/image'"
                          size="medium"
                          class="text-abb-font/85"
                        ></abb-icon>
                        }
                      </label>
                      <input
                        type="file"
                        id="imageUpload"
                        [disabled]="uiAspectForm.controls.customLogo.disabled"
                        (change)="onFileSelected($event)"
                        accept="image/*"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </abb-simple-card>
      </div>
      <div class="col-span-1 p-4 flex flex-col gap-8">
        <abb-simple-card
          [title]="'Language' | t"
          [editable]="true"
          (edit)="editLanguage($event)"
          class="flex-1"
        >
          <div
            content
            [formGroup]="form"
            class="flex-1 w-full flex flex-col gap-3 px-3 font-medium text-xs text-abb-font/80 pt-3"
          >
            <mat-form-field
              class="w-full darker-disabled-select"
              color="primary"
            >
              <mat-select
                formControlName="language"
                name="language"
                [panelClass]="'panel'"
              >
                @for (option of languageSelectOptions$ | async; track option) {
                <mat-option [value]="option.value">{{
                  option.skipTranslation ? option.label : (option.label | t)
                }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        </abb-simple-card>
      </div>
      <div class="col-span-1 p-4 flex flex-col gap-8">
        <abb-simple-card [title]="'System' | t" class="flex-1">
          <div
            content
            class="flex-1 w-full flex flex-row gap-3 px-3 font-medium text-xs text-abb-font/80 pt-2"
          >
            <div class="w-full flex flex-col flex-1 gap-4 justify-evenly">
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Version of the app' | t }}</span>
                @if (loadingSystemSettings()) {
                <mat-progress-spinner
                  class="mr-2"
                  [ngStyle]="{
                    '--mdc-circular-progress-active-indicator-color':
                      'rgb(var(--abb-color-black)/0.2)'
                  }"
                  [diameter]="16"
                  [mode]="'indeterminate'"
                ></mat-progress-spinner>
                }@else {
                <span class="text-right" [style.word-break]="'break-word'">{{
                  ('Version' | t) + ' ' + systemSettings$.value?.appVersion
                }}</span
                >}
              </div>
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{ 'Latest SCU200 firmware' | t }}</span>
                @if (loadingSystemSettings()) {
                <mat-progress-spinner
                  class="mr-2"
                  [ngStyle]="{
                    '--mdc-circular-progress-active-indicator-color':
                      'rgb(var(--abb-color-black)/0.2)'
                  }"
                  [diameter]="16"
                  [mode]="'indeterminate'"
                ></mat-progress-spinner>
                } @else {
                <span class="text-right" [style.word-break]="'break-word'">{{
                  systemSettings$.value?.scu200FwLatestVersion
                }}</span>
                }
              </div>
              <div class="flex flex-row items-center gap-2">
                <span class="flex-1">{{
                  'Current SCU200 firmware status' | t
                }}</span>
                @if (loadingSystemSettings()) {
                <mat-progress-spinner
                  class="mr-2"
                  [ngStyle]="{
                    '--mdc-circular-progress-active-indicator-color':
                      'rgb(var(--abb-color-black)/0.2)'
                  }"
                  [diameter]="16"
                  [mode]="'indeterminate'"
                ></mat-progress-spinner>
                } @else {
                <a
                  class="underline text-abb-primary/80 hover:text-abb-primary transition-colors cursor-pointer"
                  (click)="openSystemModal()"
                  >{{ 'View details' | t }}</a
                >
                }
              </div>
            </div>
          </div>
        </abb-simple-card>
      </div>
    </div>
  `,
  styles: `
    .darker-disabled-select {
      --mat-select-disabled-trigger-text-color: rgb(var(--base-value-color-font) / 0.5) !important;
      --mat-select-disabled-arrow-color: rgb(var(--base-value-color-font) / 0.5) !important;
    }

    input[type="file"] {
      display: none;
    }

    .image-upload-container {
      border: 1px dashed rgb(var(--abb-color-black) / 0.15);
    }

    .preview-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .icon-container {
      padding: calc((var(--mdc-checkbox-state-layer-size) - 24px) / 2);
      margin: calc((var(--mdc-checkbox-state-layer-size) - var(--mdc-checkbox-state-layer-size)) / 2);
      display: inline-block;
      position: relative;
      flex: 0 0 24px;
      box-sizing: content-box;
      width: 24px;
      height: 24px;
      line-height: 0;
      white-space: nowrap;
      cursor: pointer;
      vertical-align: bottom;
    }
  `,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.general' },
  ],
})
export class GeneralComponent implements OnInit, OnDestroy {
  @HostBinding('attr.id') componentid = 'general-components';
  @ViewChild('uiAspectCard') uiAspectCard: AbbSimpleCardComponent | undefined;
  private readonly prefix = inject(TRANSLATION_PREFIX);
  user: Signal<UserWithID | null>;
  regionSettings$ = new BehaviorSubject<RegionSettings | null>(null);
  languageSelectOptions$ = new BehaviorSubject<FilterOption[]>([]);
  form = new FormGroup({
    language: new FormControl<string>({ value: '', disabled: true }),
  });
  uiAspectForm = new FormGroup({
    abbBackgroundColor: new FormControl<boolean>({
      value: false,
      disabled: true,
    }),
    customBackgroundColor: new FormControl<string>({
      value: '',
      disabled: true,
    }),
    abbThemeColor: new FormControl<boolean>({ value: false, disabled: true }),
    customThemeColor: new FormControl<string>({
      value: '',
      disabled: true,
    }),
    abbTextColor: new FormControl<boolean>({ value: false, disabled: true }),
    customTextColor: new FormControl<string>({ value: '', disabled: true }),
    customLogo: new FormControl<string | ArrayBuffer | null>({
      value: null,
      disabled: true,
    }),
  });
  backgroundColor = signal<string>('');
  themeColor = signal<string>('');
  textColor = signal<string>('');
  language = signal<string>('');
  uiSettings = signal<UiSettings | null>(null);
  loadingSystemSettings = signal(true);
  systemSettings$ = new BehaviorSubject<
    SystemSettingsQuery['systemSettings'] | null
  >(null);
  webBrowserTime = computed(() => new Date());
  currentBrowserTimezone = computed(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly cognitoService: CognitoService,
    public readonly apollo: Apollo,
    private readonly translateService: TranslateService,
    private readonly theme: ThemeService,
    private readonly userService: UserSettingsService,
    private readonly toast: ToastService,
    private readonly overlayService: OverlayService
  ) {
    this.user = cognitoService.loggedUser;
    this.language.set(cognitoService.language());
    this.uiSettings.set(userService.uiSettings());
    this.subscriptions.push(
      apollo
        .watchQuery<RegionSettingsQuery>({
          query: regionSettingsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.regionSettings$.next(data.regionSettings ?? null);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translateService);
            //this.toast.notifyError(errMsg);
          },
        }),
      apollo
        .watchQuery<LanguageListQuery>({
          query: languageListQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.languageSelectOptions$.next(
              data.languageList.map((l) => ({
                value: l.id,
                label: l.name,
                skipTranslation: true,
              }))
            );
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translateService);
            //this.toast.notifyError(errMsg);
          },
        }),
      combineLatest([
        this.uiAspectForm.controls.abbThemeColor.valueChanges,
        this.uiAspectForm.controls.customThemeColor.valueChanges,
      ]).subscribe(([abb, color]) => {
        if (!abb && color) {
          this.theme.setTheme('custom', color);
        } else {
          this.theme.setTheme('default');
        }
      }),
      combineLatest([
        this.uiAspectForm.controls.abbTextColor.valueChanges,
        this.uiAspectForm.controls.customTextColor.valueChanges,
      ]).subscribe(([abb, color]) => {
        if (!abb && color) {
          this.theme.setTextColor('custom', color);
        } else {
          this.theme.setTextColor('default');
        }
      }),
      combineLatest([
        this.uiAspectForm.controls.abbBackgroundColor.valueChanges,
        this.uiAspectForm.controls.customBackgroundColor.valueChanges,
      ]).subscribe(([abb, color]) => {
        if (!abb && color) {
          this.theme.setBackgroundColor('custom', color);
        } else {
          this.theme.setBackgroundColor('default');
        }
      }),
      apollo
        .watchQuery<SystemSettingsQuery, SystemSettingsQueryVariables>({
          query: systemSettingsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.systemSettings$.next(data.systemSettings);
            this.loadingSystemSettings.set(loading);
          },
          error: (err) => {
            this.loadingSystemSettings.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  ngOnInit(): void {
    if (this.language()) {
      this.subscriptions.push(
        this.languageSelectOptions$.subscribe((languages) => {
          this.form.controls.language.setValue(
            (languages?.find((l) => {
              const langId = Object.keys(mapIdToFileName).find(
                (k) => mapIdToFileName[k] === this.language()
              );
              return l.value === langId;
            })?.value as string) ?? ''
          );
        })
      );
    } else {
      this.subscriptions.push(
        this.apollo
          .watchQuery<LanguageSettingsQuery>({
            query: languageSettingsQuery,
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              this.languageSelectOptions$.subscribe((languages) => {
                this.form.controls.language.setValue(
                  (languages?.find((l) => l.value === data.languageSettings.id)
                    ?.value as string) ?? ''
                );
              });
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translateService);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    }
    if (this.uiSettings()) {
      this.uiAspectForm.setValue({
        abbBackgroundColor: !!this.uiSettings()?.background.abb,
        customBackgroundColor: this.uiSettings()?.background.custom ?? null,
        abbThemeColor: !!this.uiSettings()?.theme.abb,
        customThemeColor: this.uiSettings()?.theme.custom ?? null,
        abbTextColor: !!this.uiSettings()?.text.abb,
        customTextColor: this.uiSettings()?.text.custom ?? null,
        customLogo: this.uiSettings()?.logo.custom ?? null,
      });
      this.imagePreview = this.uiSettings()?.logo.custom ?? null;
      this.abbLogoSelected.set(this.imagePreview ? false : true);
      this.backgroundColor.set(this.uiSettings()?.background.custom ?? '');
      this.themeColor.set(this.uiSettings()?.theme.custom ?? '');
      this.textColor.set(this.uiSettings()?.text.custom ?? '');
    } else {
      this.subscriptions.push(
        this.apollo
          .watchQuery<UiSettingsQuery>({
            query: uiSettingsQuery,
          })
          .valueChanges.subscribe({
            next: ({ data }) => {
              this.userService.setUserUiSettings({
                background: {
                  abb: !!data.uiSettings.background.abb,
                  custom: data.uiSettings.background.custom,
                },
                theme: {
                  abb: !!data.uiSettings.theme.abb,
                  custom: data.uiSettings.theme.custom,
                },
                text: {
                  abb: !!data.uiSettings.text.abb,
                  custom: data.uiSettings.text.custom,
                },
                logo: {
                  abb: data.uiSettings.logo.custom ? false : true,
                  custom: data.uiSettings.logo.custom ?? null,
                },
              });
              this.uiAspectForm.setValue({
                abbBackgroundColor: !!data.uiSettings.background.abb,
                customBackgroundColor:
                  data.uiSettings.background.custom ?? null,
                abbThemeColor: !!data.uiSettings.theme.abb,
                customThemeColor: data.uiSettings.theme.custom ?? null,
                abbTextColor: !!data.uiSettings.text.abb,
                customTextColor: data.uiSettings.text.custom ?? null,
                customLogo: data.uiSettings.logo.custom ?? null,
              });
              this.imagePreview = data.uiSettings.logo.custom ?? null;
              this.abbLogoSelected.set(this.imagePreview ? false : true);
              this.themeColor.set(data.uiSettings.theme.custom ?? '');
              this.textColor.set(data.uiSettings.text.custom ?? '');
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translateService);
              //this.toast.notifyError(errMsg);
            },
          })
      );
    }
  }

  editLanguage(editing: boolean) {
    console.log(this.form.value.language, 'this.form.value.language');

    if (!editing) {
      if (this.form.invalid) {
        return;
      }
      this.subscriptions.push(
        this.apollo
          .mutate<
            UpdateLanguageSettingsMutation,
            UpdateLanguageSettingsMutationVariables
          >({
            mutation: languageSettingsMutation,
            variables: {
              languageId: this.form.value.language!,
            },
          })
          .subscribe({
            next: ({ data }) => {
              const appLang =
                mapIdToFileName[this.form.value.language ?? '1'] || 'en-EN';
              if (appLang === 'ja-JP' || appLang.startsWith('ja')) {
                document.body.classList.add('jp-font');
              } else {
                document.body.classList.remove('jp-font');
              }
              this.cognitoService.setAppLanguage(appLang);
              this.translateService.use(appLang).subscribe({
                next: () => {
                  console.log(`Language set to: ${appLang}`);
                },
                error: (err) => {
                  console.error('Error initializing translations:', err);
                },
              });
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translateService);
              //this.toast.notifyError(errMsg);
            },
          })
      );
      this.form.controls.language.disable();
    } else {
      this.form.controls.language.enable();
    }
  }

  editUIAspect(editing: boolean) {
    if (!editing) {
      if (this.uiAspectForm.invalid) {
        return;
      }
      if (!this.checkIfFormIsValid()) {
        this.uiAspectCard?.editing.set(true);
        this.showWarningMessages();
        return;
      }
      this.subscriptions.push(
        this.apollo
          .mutate<UpdateUiSettingsMutation, UpdateUiSettingsMutationVariables>({
            mutation: uiSettingsMutation,
            variables: {
              abbBackgroundColor: !!this.uiAspectForm.value.abbBackgroundColor,
              customBackgroundColor:
                this.uiAspectForm.value.customBackgroundColor,
              abbThemeColor: !!this.uiAspectForm.value.abbThemeColor,
              customThemeColor: this.uiAspectForm.value.customThemeColor,
              abbTextColor: !!this.uiAspectForm.value.abbTextColor,
              customTextColor: this.uiAspectForm.value.customTextColor,
              customLogo: this.uiAspectForm.value.customLogo as string,
            },
          })
          .subscribe({
            next: ({ data }) => {
              let customLogo = data?.updateUiSettings.logo.custom ?? null;
              if (customLogo && !customLogo.startsWith('data:image/')) {
                customLogo = 'data:image/png;base64,' + customLogo;
              }

              this.userService.setUserUiSettings({
                background: {
                  abb: !!data?.updateUiSettings.background.abb,
                  custom: data?.updateUiSettings.background.custom,
                },
                theme: {
                  abb: !!data?.updateUiSettings.theme.abb,
                  custom: data?.updateUiSettings.theme.custom,
                },
                text: {
                  abb: !!data?.updateUiSettings.text.abb,
                  custom: data?.updateUiSettings.text.custom,
                },
                logo: {
                  abb: data?.updateUiSettings.logo.custom ? false : true,
                  custom: customLogo,
                },
              });
              this.apollo.client.refetchQueries({
                include: ['GeneralSettings'],
              });
              this.uiAspectForm.controls.abbBackgroundColor.disable();
              this.uiAspectForm.controls.abbTextColor.disable();
              this.uiAspectForm.controls.abbThemeColor.disable();
              this.uiAspectForm.controls.customBackgroundColor.disable();
              this.uiAspectForm.controls.customTextColor.disable();
              this.uiAspectForm.controls.customThemeColor.disable();
              this.uiAspectForm.controls.customLogo.disable();
            },
            error: (err) => {
              //let errMsg = getErrorMessage(err, this.translateService);
              //this.toast.notifyError(errMsg);
              this.uiAspectCard?.editing.set(true);
            },
          })
      );
    } else {
      this.uiAspectForm.controls.abbBackgroundColor.enable();
      this.uiAspectForm.controls.abbTextColor.enable();
      this.uiAspectForm.controls.abbThemeColor.enable();
      this.uiAspectForm.controls.customBackgroundColor.enable();
      this.uiAspectForm.controls.customTextColor.enable();
      this.uiAspectForm.controls.customThemeColor.enable();
      this.uiAspectForm.controls.customLogo.enable();
    }
  }

  backgroundColorChange(event: MatCheckboxChange, formControlName: string) {
    if (formControlName === 'customBackgroundColor') {
      this.uiAspectForm.patchValue({ abbBackgroundColor: !event.checked });
    } else {
      if (event.checked) {
        this.backgroundColor.set('');
        this.uiAspectForm.patchValue({ customBackgroundColor: null });
      }
    }
  }

  themeColorChange(event: MatCheckboxChange, formControlName: string) {
    if (formControlName === 'customThemeColor') {
      this.uiAspectForm.patchValue({ abbThemeColor: !event.checked });
    } else {
      if (event.checked) {
        this.themeColor.set('');
        this.uiAspectForm.patchValue({ customThemeColor: null });
      }
    }
  }

  colorSelect(color: string, field: 'theme' | 'text' | 'background') {
    if (field === 'theme') {
      this.themeColor.set(color);
      this.uiAspectForm.patchValue({ customThemeColor: color });
    } else if (field === 'text') {
      this.textColor.set(color);
      this.uiAspectForm.patchValue({ customTextColor: color });
    } else {
      this.backgroundColor.set(color);
      this.uiAspectForm.patchValue({ customBackgroundColor: color });
    }
  }

  textColorChange(event: MatCheckboxChange, formControlName: string) {
    if (formControlName === 'customTextColor') {
      this.uiAspectForm.patchValue({ abbTextColor: !event.checked });
    } else {
      if (event.checked) {
        this.textColor.set('');
        this.uiAspectForm.patchValue({ customTextColor: null });
      }
    }
  }

  imagePreview: string | ArrayBuffer | null = null;
  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
        this.uiAspectForm.patchValue({ customLogo: this.imagePreview });
        this.abbLogoSelected.set(false);
      };
      reader.readAsDataURL(file);
    }
  }

  abbLogoSelected = signal(true);
  selectABBLogo() {
    this.imagePreview = null;
    this.uiAspectForm.patchValue({ customLogo: null });
    this.abbLogoSelected.set(true);
  }

  openSystemModal() {
    this.overlayService.openModal(SystemSettingsModalComponent, {
      systemSettings: this
        .systemSettings$ as BehaviorSubject<SystemSettings | null>,
    });
  }

  openEditRegionModal() {
    this.overlayService.openModal(RegionSettingsModalComponent, {
      regionSettings$: this.regionSettings$,
      webBrowserTime: this.webBrowserTime(),
      currentBrowserTimezone: this.currentBrowserTimezone(),
    });
  }

  checkIfFormIsValid(): boolean {
    const form = this.uiAspectForm.controls;
    return (
      (form.abbBackgroundColor.value
        ? form.customBackgroundColor.value === null
        : form.customBackgroundColor.value !== null) &&
      (form.abbThemeColor.value
        ? form.customThemeColor.value === null
        : form.customThemeColor.value !== null) &&
      (form.abbTextColor.value
        ? form.customTextColor.value === null
        : form.customTextColor.value !== null)
    );
  }

  showWarningMessages() {
    const form = this.uiAspectForm.controls;
    if (
      !form.abbBackgroundColor.value &&
      form.customBackgroundColor.value === null
    ) {
      this.toast.notifyWarning(
        t(
          this.prefix,
          `Please select a background color`,
          this.translateService
        )
      );
    }
    if (!form.abbThemeColor.value && form.customThemeColor.value === null) {
      this.toast.notifyWarning(
        t(this.prefix, `Please select a theme color`, this.translateService)
      );
    }
    if (!form.abbTextColor.value && form.customTextColor.value === null) {
      this.toast.notifyWarning(
        t(this.prefix, `Please select a text color`, this.translateService)
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
